import React, { useState, useEffect, useContext, useRef } from 'react';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import EducationForm from '@/components/student/EducationForm';
import { 
  BookOpen, Award, ClipboardCheck, ClipboardList, User, Calendar, Clock, PlayCircle, 
  FileText, Download, AlertCircle, CheckCircle, XCircle, RefreshCw, IndianRupee, 
  GraduationCap, CreditCard, CalendarClock, Building2, Globe, Briefcase, Bell,
  ChevronDown, LogOut, Settings, Search, TrendingUp, Target, Zap, ArrowRight,
  Eye, MoreVertical, MapPin, Phone, Mail, Star, ChevronRight, ExternalLink,
  Wallet, Receipt, BadgeCheck, Trophy, Sparkles, BookMarked, PenLine, Plus, UserCircle,
  Video, CalendarDays
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StudentDashboard = () => {
  const { user, isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const tabsSectionRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Close dropdown on route change
  useEffect(() => {
    setShowProfileMenu(false);
    setShowNotifications(false);
  }, [location.pathname]);

  // Click outside handler for profile dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setShowProfileMenu(false);
        setShowNotifications(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showProfileMenu]);

  // Function to change tab and scroll to it
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    setTimeout(() => {
      tabsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Data states
  const [enrollments, setEnrollments] = useState([]);
  const [results, setResults] = useState([]);
  const [courses, setCourses] = useState([]);
  const [distanceEnrollments, setDistanceEnrollments] = useState([]);
  const [distancePayments, setDistancePayments] = useState([]);
  const [admissions, setAdmissions] = useState([]);
  const [studentFees, setStudentFees] = useState([]);
  const [lmsEnrollments, setLmsEnrollments] = useState([]);
  const [lmsCourses, setLmsCourses] = useState([]);
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [myInterviews, setMyInterviews] = useState([]);
  const [educationData, setEducationData] = useState(null);
  const [showEducationForm, setShowEducationForm] = useState(false);
  const [profileData, setProfileData] = useState(null);
  // Live center-side data (single source of truth bridge).
  const [centerSyncData, setCenterSyncData] = useState({
    linked: false, profile: null, summary: null,
    courses: [], fees: null, certificates: [],
  });
  
  // Payment states
  const [payingYear, setPayingYear] = useState(null);
  const [payingAdmission, setPayingAdmission] = useState(null);
  const [payingInstallment, setPayingInstallment] = useState(null);

  // Real-time notifications/activity feed
  const [activityFeed, setActivityFeed] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Icon mapping for activity feed
  const activityIconMap = {
    ClipboardCheck: ClipboardCheck,
    CreditCard: CreditCard,
    Award: Award,
    AlertCircle: AlertCircle,
    Briefcase: Briefcase,
    Calendar: Calendar
  };

  // WebSocket connection for real-time updates
  const connectWebSocket = () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/student?token=${token}`);
    
    ws.onopen = () => {
      console.log('WebSocket connected for student dashboard');
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 25000);
      ws.pingInterval = pingInterval;
    };
    
    ws.onmessage = (event) => {
      if (event.data === 'pong') return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'activity') {
          setActivityFeed(prev => {
            const newActivity = {
              id: `ws_${Date.now()}`,
              type: data.activity_type,
              message: data.message,
              time: data.time,
              icon: data.activity_type === 'exam_result' ? 'Award' : 'ClipboardCheck',
              color: data.activity_type === 'exam_result' ? 'emerald' : 'blue'
            };
            return [newActivity, ...prev.slice(0, 9)];
          });
          toast.info(data.message, { duration: 4000 });
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };
    
    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (ws.pingInterval) clearInterval(ws.pingInterval);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    wsRef.current = ws;
  };

  // Fetch real-time activity feed
  const fetchActivityFeed = async () => {
    try {
      const res = await axios.get(`${API}/activity/student`);
      setActivityFeed(res.data || []);
    } catch (error) {
      console.error('Failed to fetch activity feed:', error);
      setActivityFeed([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Fetch education data
  const fetchEducation = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/student/education`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEducationData(res.data);
    } catch (error) {
      console.error('Failed to fetch education:', error);
      setEducationData(null);
    }
  };

  // Use activity feed for notifications (fallback to mock)
  const notifications = activityFeed.length > 0 ? activityFeed.slice(0, 3).map(a => ({
    id: a.id,
    type: a.type,
    message: a.message,
    time: a.time
  })) : [
    { id: 1, type: 'exam', message: 'Exam scheduled for Digital Marketing', time: '2 hours ago' },
    { id: 2, type: 'fee', message: 'Fee payment reminder: ₹5,000 due', time: '1 day ago' },
    { id: 3, type: 'job', message: '3 new jobs matching your profile', time: '2 days ago' }
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth/login');
      return;
    }
    fetchData();
    fetchActivityFeed();
    connectWebSocket();
    
    // Auto-refresh activity feed every 30 seconds (fallback)
    const activityInterval = setInterval(fetchActivityFeed, 30000);
    
    return () => {
      clearInterval(activityInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, navigate]);

  // Auto-refresh on tab focus — keeps Student Dashboard in sync with any
  // Center Dashboard updates without manual reload. Throttled to 3s.
  useAutoRefresh(() => { if (!loading) fetchData(); }, { throttleMs: 3000 });

  const fetchData = async () => {
    setLoading(true);    try {
      const [enrollmentsRes, resultsRes, coursesRes] = await Promise.all([
        axios.get(`${API}/assessment/my-enrollments`),
        axios.get(`${API}/assessment/my-results`),
        axios.get(`${API}/assessment/courses`)
      ]);
      
      setEnrollments(enrollmentsRes.data);
      setCourses(coursesRes.data);

      // Also fetch dynamic exam results
      try {
        const dynamicExamRes = await axios.get(`${API}/dynamic-exam/my-attempts`);
        // Merge dynamic exam results with assessment results
        const dynamicResults = dynamicExamRes.data
          .filter(a => a.status === 'completed' || a.status === 'timed_out')
          .map(a => ({
            id: a.id,
            course_id: a.course_id,
            course_name: a.course_name,
            result: a.result,
            percentage: a.percentage,
            marks_obtained: a.marks_obtained,
            total_marks: a.total_marks,
            certificate_id: null, // Will be fetched separately if needed
            training_mode: a.training_mode
          }));
        setResults([...resultsRes.data, ...dynamicResults]);
      } catch (e) {
        setResults(resultsRes.data);
      }

      // Fetch other data - Distance Education Enrollments with yearly fee status
      try {
        const token = localStorage.getItem('token');
        const [distEnrollRes, distPayHistoryRes] = await Promise.all([
          axios.get(`${API}/student/distance-enrollments`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/student/yearly-fee/history`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setDistanceEnrollments(distEnrollRes.data || []);
        setDistancePayments(distPayHistoryRes.data || []);
      } catch (e) {
        console.log('Distance enrollments fetch error:', e);
        setDistanceEnrollments([]);
        setDistancePayments([]);
      }

      try {
        const admissionsRes = await axios.get(`${API}/admissions/my-admissions`);
        setAdmissions(admissionsRes.data);
      } catch (e) {}

      try {
        const feesRes = await axios.get(`${API}/fees/my-fees`);
        setStudentFees(feesRes.data);
      } catch (e) {}

      try {
        const [lmsEnrollRes, lmsCoursesRes] = await Promise.all([
          axios.get(`${API}/lms/my-enrollments`),
          axios.get(`${API}/lms/courses?published_only=true`)
        ]);
        setLmsEnrollments(lmsEnrollRes.data);
        setLmsCourses(lmsCoursesRes.data);
      } catch (e) {}

      // Fetch recommended jobs
      try {
        const jobsRes = await axios.get(`${API}/jobs?limit=6`);
        setRecommendedJobs(Array.isArray(jobsRes.data) ? jobsRes.data : (jobsRes.data?.jobs || []));
      } catch (e) {}

      // Fetch my interviews
      try {
        const interviewsRes = await axios.get(`${API}/jobs/my-interviews`);
        setMyInterviews(Array.isArray(interviewsRes.data) ? interviewsRes.data : []);
      } catch (e) {}

      // Fetch education data
      await fetchEducation();

      // Fetch profile data for completion calculation
      try {
        const profileRes = await axios.get(`${API}/profile/student`);
        setProfileData(profileRes.data);
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('profile fetch:', e?.response?.status);
      }

      // ★ SINGLE SOURCE OF TRUTH BRIDGE — pulls live data from center_students.
      // Backed by /app/backend/routers/student_sync.py — joins course, fees,
      // center, and computes course end date dynamically. Always reflects the
      // latest Center Dashboard update without manual refresh.
      try {
        const [csProfile, csSummary, csCourses, csFees, csCerts] = await Promise.all([
          axios.get(`${API}/student/center-profile`),
          axios.get(`${API}/student/center-summary`),
          axios.get(`${API}/student/center-courses`),
          axios.get(`${API}/student/center-fees`),
          axios.get(`${API}/student/center-certificates`),
        ]);
        setCenterSyncData({
          linked: !!csProfile.data?.linked,
          profile: csProfile.data,
          summary: csSummary.data,
          courses: csCourses.data || [],
          fees: csFees.data,
          certificates: csCerts.data || [],
        });
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('center-sync fetch:', e?.response?.status);
      }

    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEnrollmentWithCourse = (enrollment) => {
    const course = courses.find(c => c.id === enrollment.course_id);
    return { ...enrollment, course };
  };

  const getResultWithCourse = (result) => {
    const course = courses.find(c => c.id === result.course_id);
    return { ...result, course };
  };

  // Calculate stats
  const passedCourses = results.filter(r => r.result === 'pass');
  const failedCourses = results.filter(r => r.result === 'fail');
  const pendingExams = enrollments.filter(e => 
    !results.some(r => r.course_id === courses.find(c => c.id === e.course_id)?.id)
  );

  // Merge in live center_students data — so an offline-center-enrolled student
  // never sees zeros while data exists on the Center side.
  const syncSummary = centerSyncData?.summary || {};
  const syncCourses = centerSyncData?.courses || [];
  const syncCerts = centerSyncData?.certificates || [];
  const syncFees = centerSyncData?.fees || {};

  const totalFeesPaid = (studentFees.reduce((sum, f) => sum + (f.total_paid || 0), 0))
    || syncFees.paid || syncSummary.fees_paid || 0;
  const totalFeesPending = (studentFees.reduce((sum, f) => sum + (f.total_pending || 0), 0))
    || syncFees.pending || syncSummary.fees_pending || 0;

  // Course count: prefer real assessment enrollments; fall back to center sync.
  const myCoursesCount = (enrollments.length + lmsEnrollments.length + syncCourses.length) ||
    (syncSummary.my_courses || 0);
  const certificatesCount = passedCourses.length || syncCerts.length || syncSummary.certificates || 0;

  // Current course (first enrolled course with progress)
  const currentCourse = lmsEnrollments[0] || null;
  const currentCourseProgress = currentCourse?.progress_percent || 0;

  // Payment handlers (simplified)
  const handleFeeInstallmentPayment = async (fee, installmentNumber) => {
    setPayingInstallment(`${fee.id}-${installmentNumber}`);
    try {
      const orderRes = await axios.post(`${API}/fees/create-payment-order?student_fee_id=${fee.id}&installment_number=${installmentNumber}`);
      const orderData = orderRes.data;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.total_amount * 100,
        currency: orderData.currency,
        name: 'Iqra Skills',
        description: `${orderData.course_name} - Installment ${installmentNumber}`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/fees/verify-payment?student_fee_id=${fee.id}&installment_number=${installmentNumber}`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(`Installment ${installmentNumber} paid successfully!`);
            fetchData();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { name: user?.name || '', email: user?.email || '' },
        theme: { color: '#059669' }
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setPayingInstallment(null);
    }
  };

  // Handle yearly fee payment for Distance Education courses
  const handleYearlyFeePayment = async (enrollment, year, amount) => {
    setPayingYear(`${enrollment.enrollment_id}-${year}`);
    try {
      const token = localStorage.getItem('token');
      
      // Create Razorpay order for yearly fee
      const orderRes = await axios.post(`${API}/payment/create-order`, {
        amount: amount,
        course_name: enrollment.course_name,
        description: `Year ${year} Fee - ${enrollment.course_name}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const orderData = orderRes.data;
      
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount * 100,
        currency: 'INR',
        name: 'Iqra Skills',
        description: `${enrollment.course_name} - Year ${year} Fee`,
        order_id: orderData.order_id,
        handler: async function (response) {
          try {
            // Record payment
            await axios.post(`${API}/student/yearly-fee/pay`, {
              enrollment_id: enrollment.enrollment_id,
              year: year,
              amount: amount,
              payment_method: 'online',
              transaction_id: response.razorpay_payment_id
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(`Year ${year} fee of ₹${amount.toLocaleString()} paid successfully! ✅`);
            fetchData(); // Refresh data
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: { 
          name: user?.name || '', 
          email: user?.email || '' 
        },
        theme: { color: '#7c3aed' }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPayingYear(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Modern Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm" data-testid="student-header">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Left: Logo & Branding */}
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/iqraskills-logo.png" 
                  alt="Iqra Skills Logo" 
                  className="w-10 h-10 rounded-full object-cover"
                />
                <span className="font-bold text-xl hidden sm:block">
                  <span className="text-blue-600">Iqra</span>
                  <span className="text-emerald-600"> Skills</span>
                </span>
              </Link>
              {currentCourse && (
                <span className="hidden md:inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full text-sm font-medium">
                  <BookMarked className="w-4 h-4" />
                  {currentCourse.course_title?.substring(0, 20)}...
                </span>
              )}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                    <div className="px-4 py-2 border-b border-slate-100">
                      <h3 className="font-semibold text-slate-800">Notifications</h3>
                    </div>
                    {notifications.map(notif => (
                      <div key={notif.id} className="px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50">
                        <p className="text-sm text-slate-700">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{notif.time}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div className="relative" ref={profileDropdownRef}>
                <button 
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className={`flex items-center gap-2 p-2 rounded-xl transition-all duration-200 ${
                    showProfileMenu ? 'bg-slate-100 ring-2 ring-blue-500/20' : 'hover:bg-slate-100'
                  }`}
                  data-testid="profile-menu"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {user?.name?.charAt(0) || 'S'}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-800">{user?.name || 'Student'}</p>
                    <p className="text-xs text-slate-500">Student</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 hidden sm:block transition-transform duration-200 ${showProfileMenu ? 'rotate-180' : ''}`} />
                </button>
                
                {showProfileMenu && (
                  <div 
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-purple-50">
                      <p className="font-medium text-slate-800">{user?.name}</p>
                      <p className="text-sm text-slate-500">{user?.email}</p>
                    </div>
                    <button 
                      onClick={() => { navigate('/student/profile'); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                    >
                      <User className="w-4 h-4" /> Profile
                    </button>
                    <button 
                      onClick={() => { navigate('/student/certificates'); setShowProfileMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                    >
                      <Award className="w-4 h-4" /> Certificates
                    </button>
                    <button 
                      onClick={() => setShowProfileMenu(false)}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2 transition-colors"
                    >
                      <Settings className="w-4 h-4" /> Settings
                    </button>
                    <hr className="my-2" />
                    <button 
                      onClick={handleLogout}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        
        {/* Welcome Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-500 rounded-2xl p-6 lg:p-8 text-white mb-6 relative overflow-hidden" data-testid="welcome-section">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-4">
              {/* Profile photo — live from center_students (auto-syncs) */}
              {centerSyncData?.profile?.student?.photo ? (
                <img
                  src={centerSyncData.profile.student.photo}
                  alt={centerSyncData.profile.student.name || 'Student'}
                  className="w-16 h-16 rounded-2xl object-cover border-2 border-white/40 shadow-lg"
                  data-testid="welcome-student-photo"
                />
              ) : (
                <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                  <span className="text-3xl">👋</span>
                </div>
              )}
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">
                  Welcome back, {(centerSyncData?.profile?.student?.name || user?.name)?.split(' ')[0] || 'Student'}!
                </h1>
                <p className="text-white/80 mt-1">Continue your learning journey</p>
                {(centerSyncData?.profile?.student?.student_id || profileData?.enrollment_number) && (
                  <p className="text-sm font-mono text-white/90 mt-1 bg-white/15 inline-block px-3 py-0.5 rounded-full" data-testid="dashboard-enrollment-number">
                    Enrollment No: {centerSyncData?.profile?.student?.student_id || profileData.enrollment_number}
                  </p>
                )}
              </div>
            </div>
            
            {currentCourse ? (
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 min-w-[300px]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white/80">Current Course</span>
                  <span className="text-sm font-bold">{currentCourseProgress.toFixed(0)}%</span>
                </div>
                <h3 className="font-semibold mb-2">{currentCourse.course_title}</h3>
                <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden mb-3">
                  <div 
                    className="h-full bg-white rounded-full transition-all" 
                    style={{ width: `${currentCourseProgress}%` }}
                  ></div>
                </div>
                <Button 
                  onClick={() => navigate(`/student/learn/${currentCourse.id}`)}
                  className="w-full bg-white text-purple-600 hover:bg-white/90"
                >
                  <PlayCircle className="w-4 h-4 mr-2" /> Continue Learning
                </Button>
              </div>
            ) : (
              <Button 
                onClick={() => navigate('/student/admission')}
                className="bg-white text-purple-600 hover:bg-white/90 px-6"
              >
                <BookOpen className="w-4 h-4 mr-2" /> Browse Courses
              </Button>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="quick-actions">
          <button 
            onClick={() => handleTabChange('courses')}
            className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all group text-left"
          >
            <div className="p-3 bg-blue-100 rounded-xl w-fit mb-3 group-hover:bg-blue-200 transition-colors">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-800">My Courses</h3>
            <p className="text-sm text-slate-500 mt-1">{myCoursesCount} enrolled</p>
          </button>
          
          <button 
            onClick={() => navigate('/student/exams')}
            className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all group text-left"
          >
            <div className="p-3 bg-purple-100 rounded-xl w-fit mb-3 group-hover:bg-purple-200 transition-colors">
              <ClipboardCheck className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Take Exam</h3>
            <p className="text-sm text-slate-500 mt-1">{pendingExams.length} pending</p>
          </button>
          
          <button 
            onClick={() => navigate('/student/certificates')}
            className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all group text-left"
          >
            <div className="p-3 bg-emerald-100 rounded-xl w-fit mb-3 group-hover:bg-emerald-200 transition-colors">
              <Award className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Certificates</h3>
            <p className="text-sm text-slate-500 mt-1">{certificatesCount} earned</p>
          </button>
          
          <button 
            onClick={() => navigate('/jobs')}
            className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all group text-left"
          >
            <div className="p-3 bg-amber-100 rounded-xl w-fit mb-3 group-hover:bg-amber-200 transition-colors">
              <Briefcase className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Apply for Jobs</h3>
            <p className="text-sm text-slate-500 mt-1">{recommendedJobs.length} available</p>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="stats-cards">
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Enrolled Courses</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">{myCoursesCount}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Exams Passed</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">{certificatesCount}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <Trophy className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Fees Paid</p>
                <p className="text-3xl font-bold text-slate-800 mt-1">₹{(totalFeesPaid / 1000).toFixed(0)}K</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-xl">
                <Wallet className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className={`rounded-2xl p-5 shadow-sm ${totalFeesPending > 0 ? 'bg-gradient-to-br from-amber-500 to-amber-600 text-white' : 'bg-white border border-slate-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${totalFeesPending > 0 ? 'text-amber-100' : 'text-slate-500'}`}>Pending Fees</p>
                <p className={`text-3xl font-bold mt-1 ${totalFeesPending > 0 ? 'text-white' : 'text-slate-800'}`}>
                  ₹{(totalFeesPending / 1000).toFixed(0)}K
                </p>
              </div>
              <div className={`p-3 rounded-xl ${totalFeesPending > 0 ? 'bg-white/20' : 'bg-amber-100'}`}>
                <CreditCard className={`w-6 h-6 ${totalFeesPending > 0 ? 'text-white' : 'text-amber-600'}`} />
              </div>
            </div>
            {totalFeesPending > 0 && (
              <Button 
                onClick={() => setActiveTab('fees')}
                size="sm"
                className="mt-3 bg-white/20 hover:bg-white/30 text-white w-full"
              >
                Pay Now →
              </Button>
            )}
          </div>
        </div>

        {/* Profile Completion Mini Widget */}
        {(() => {
          // Calculate profile completion
          const fields = [
            { key: 'photo_url', filled: !!profileData?.photo_url },
            { key: 'father_name', filled: !!profileData?.father_name },
            { key: 'mother_name', filled: !!profileData?.mother_name },
            { key: 'dob', filled: !!profileData?.dob },
            { key: 'qualification', filled: !!profileData?.qualification },
            { key: 'address', filled: !!profileData?.address },
            { key: 'city', filled: !!profileData?.city },
            { key: 'district', filled: !!profileData?.district },
            { key: 'state', filled: !!profileData?.state },
            { key: 'pin_code', filled: !!profileData?.pin_code },
            { key: 'education', filled: !!educationData?.highest_qualification }
          ];
          const filledCount = fields.filter(f => f.filled).length;
          const percentage = Math.round((filledCount / fields.length) * 100);
          
          if (percentage === 100) return null; // Don't show if complete
          
          return (
            <div 
              onClick={() => navigate('/student/profile')}
              className={`rounded-2xl p-5 mb-6 cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01] ${
                percentage >= 70 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500' 
                  : 'bg-gradient-to-r from-orange-500 to-amber-500'
              }`}
              data-testid="profile-completion-widget"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                    <UserCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Complete Your Profile</h3>
                    <p className="text-white/80 text-sm">
                      {percentage}% complete • {fields.length - filledCount} fields remaining
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {/* Mini Progress Circle */}
                  <div className="relative w-16 h-16 hidden sm:block">
                    <svg className="w-16 h-16 transform -rotate-90">
                      <circle cx="32" cy="32" r="28" stroke="rgba(255,255,255,0.2)" strokeWidth="5" fill="none" />
                      <circle 
                        cx="32" cy="32" r="28" stroke="white" strokeWidth="5" fill="none" 
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${2 * Math.PI * 28 * (1 - percentage / 100)}`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-bold text-white">{percentage}%</span>
                    </div>
                  </div>
                  <Button className="bg-white/20 hover:bg-white/30 text-white border-0">
                    Complete Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
              {/* Progress Bar */}
              <div className="w-full h-1.5 bg-white/20 rounded-full mt-4 overflow-hidden">
                <div className="h-full bg-white rounded-full transition-all" style={{ width: `${percentage}%` }}></div>
              </div>
            </div>
          );
        })()}

        {/* Learning Journey Flow */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm" data-testid="learning-journey">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-500" />
            Your Learning Journey
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-3">
            {[
              { step: 1, title: 'Choose Course', icon: ClipboardList, color: 'emerald', action: () => navigate('/student/admission') },
              { step: 2, title: 'Pay Fees', icon: IndianRupee, color: 'amber', action: () => setActiveTab('fees') },
              { step: 3, title: 'Learn', icon: PlayCircle, color: 'purple', action: () => setActiveTab('lms-courses') },
              { step: 4, title: 'Take Exam', icon: ClipboardCheck, color: 'blue', action: () => navigate('/student/exams') },
              { step: 5, title: 'Certificate', icon: Award, color: 'yellow', action: () => navigate('/student/certificates') },
              { step: 6, title: 'Get Placed', icon: Briefcase, color: 'green', action: () => navigate('/student/placements') }
            ].map((item, index) => (
              <React.Fragment key={item.step}>
                <button 
                  onClick={item.action}
                  className={`flex items-center gap-2 px-4 py-3 bg-${item.color}-50 hover:bg-${item.color}-100 rounded-xl border border-${item.color}-200 transition-all group cursor-pointer flex-1 min-w-[140px]`}
                >
                  <div className={`w-7 h-7 bg-${item.color}-500 rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                    {item.step}
                  </div>
                  <span className={`text-sm font-medium text-${item.color}-700`}>{item.title}</span>
                </button>
                {index < 5 && <ChevronRight className="w-5 h-5 text-slate-300 hidden lg:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Main Tabs */}
        <div ref={tabsSectionRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-slate-200 mb-6 p-1 rounded-xl flex-wrap">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="courses" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              My Courses
            </TabsTrigger>
            <TabsTrigger value="lms-courses" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Video Learning
            </TabsTrigger>
            <TabsTrigger value="fees" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Fees & Payment
            </TabsTrigger>
            <TabsTrigger value="yearly-fees" className="rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              Yearly Fees
            </TabsTrigger>
            <TabsTrigger value="exams" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Exams & Certificates
            </TabsTrigger>
            <TabsTrigger value="education" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              My Education
            </TabsTrigger>
            <TabsTrigger value="jobs" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Jobs & Placement
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              Profile
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* My Courses Preview */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-500" />
                    My Courses
                  </h3>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab('courses')} className="rounded-lg">
                    View All
                  </Button>
                </div>
                
                {lmsEnrollments.length === 0 && enrollments.length === 0 && syncCourses.length === 0 ? (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No courses enrolled yet</p>
                    <Button onClick={() => navigate('/student/admission')} className="mt-3">Browse Courses</Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Center-side enrolled courses (live join) */}
                    {syncCourses.map((c) => (
                      <div key={`sync-${c.id || c.name}`} className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-xl border border-blue-100">
                        <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-800 truncate">{c.name}</h4>
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                            <span>{c.duration || '—'}</span>
                            {c.end_date && (
                              <span>Valid till {new Date(c.end_date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</span>
                            )}
                            <span className="text-emerald-700 font-semibold">{c.status || 'active'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {lmsEnrollments.slice(0, 3).map(enrollment => (
                      <div key={enrollment.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                        <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                          <PlayCircle className="w-8 h-8 text-purple-500" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-slate-800">{enrollment.course_title}</h4>
                          <div className="flex items-center gap-4 mt-1">
                            <span className="text-sm text-slate-500">{enrollment.progress_percent?.toFixed(0) || 0}% complete</span>
                            <div className="flex-1 h-2 bg-slate-200 rounded-full max-w-[150px]">
                              <div className="h-full bg-purple-500 rounded-full" style={{ width: `${enrollment.progress_percent || 0}%` }}></div>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => navigate(`/student/learn/${enrollment.id}`)} className="bg-purple-600 hover:bg-purple-700">
                          Continue
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notifications Panel */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="notifications-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-red-500" />
                    Notifications
                  </h3>
                  <button onClick={fetchActivityFeed} className="text-blue-500 hover:text-blue-600 transition-colors">
                    <RefreshCw className={`w-4 h-4 ${activityLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <div className="space-y-3">
                  {activityLoading ? (
                    <div className="text-center py-4">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
                      <p className="text-xs text-slate-500 mt-2">Loading...</p>
                    </div>
                  ) : activityFeed.length > 0 ? (
                    activityFeed.map(activity => {
                      const IconComponent = activityIconMap[activity.icon] || Bell;
                      return (
                        <div 
                          key={activity.id} 
                          className={`flex items-start gap-3 p-3 rounded-xl border ${
                            activity.color === 'blue' ? 'bg-blue-50 border-blue-200' :
                            activity.color === 'amber' ? 'bg-amber-50 border-amber-200' :
                            activity.color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
                            activity.color === 'red' ? 'bg-red-50 border-red-200' :
                            activity.color === 'purple' ? 'bg-purple-50 border-purple-200' :
                            'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <IconComponent className={`w-5 h-5 mt-0.5 ${
                            activity.color === 'blue' ? 'text-blue-500' :
                            activity.color === 'amber' ? 'text-amber-500' :
                            activity.color === 'emerald' ? 'text-emerald-500' :
                            activity.color === 'red' ? 'text-red-500' :
                            activity.color === 'purple' ? 'text-purple-500' :
                            'text-slate-500'
                          }`} />
                          <div>
                            <p className={`text-sm font-medium ${
                              activity.color === 'blue' ? 'text-blue-700' :
                              activity.color === 'amber' ? 'text-amber-700' :
                              activity.color === 'emerald' ? 'text-emerald-700' :
                              activity.color === 'red' ? 'text-red-700' :
                              activity.color === 'purple' ? 'text-purple-700' :
                              'text-slate-700'
                            }`}>{activity.message}</p>
                            <p className={`text-xs ${
                              activity.color === 'blue' ? 'text-blue-500' :
                              activity.color === 'amber' ? 'text-amber-500' :
                              activity.color === 'emerald' ? 'text-emerald-500' :
                              activity.color === 'red' ? 'text-red-500' :
                              activity.color === 'purple' ? 'text-purple-500' :
                              'text-slate-500'
                            }`}>{activity.time}</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <>
                      {pendingExams.length > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                          <ClipboardCheck className="w-5 h-5 text-blue-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-700">{pendingExams.length} exam(s) pending</p>
                            <p className="text-xs text-blue-500">Complete your assessments</p>
                          </div>
                        </div>
                      )}
                      {totalFeesPending > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-200">
                          <CreditCard className="w-5 h-5 text-amber-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-amber-700">Fee payment due</p>
                            <p className="text-xs text-amber-500">₹{totalFeesPending.toLocaleString()} pending</p>
                          </div>
                        </div>
                      )}
                      {failedCourses.length > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-red-50 rounded-xl border border-red-200">
                          <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-red-700">{failedCourses.length} reassessment needed</p>
                            <p className="text-xs text-red-500">Retry your exams</p>
                          </div>
                        </div>
                      )}
                      {recommendedJobs.length > 0 && (
                        <div className="flex items-start gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                          <Briefcase className="w-5 h-5 text-emerald-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-emerald-700">{recommendedJobs.length} jobs for you</p>
                            <p className="text-xs text-emerald-500">Based on your skills</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* My Courses Tab */}
          <TabsContent value="courses">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800">All Enrolled Courses</h3>
                <Button onClick={() => navigate('/courses')} variant="outline">Browse More</Button>
              </div>
              
              {/* Pending/Failed Admissions - Retry Payment Section */}
              {admissions.filter(a => ['pending', 'payment_failed', 'failed'].includes(a.status)).length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Pending Payments
                  </h4>
                  <div className="space-y-3">
                    {admissions.filter(a => ['pending', 'payment_failed', 'failed'].includes(a.status)).map(admission => (
                      <div key={admission.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-amber-100">
                        <div>
                          <p className="font-medium text-slate-800">{admission.course_name}</p>
                          <p className="text-sm text-slate-500">Fee: ₹{admission.course_fee?.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            admission.status === 'payment_failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {admission.status === 'payment_failed' ? 'Payment Failed' : 'Payment Pending'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admissions/${admission.id}/cancel`);
                                toast.success('Admission cancelled');
                                fetchData();
                              } catch (e) {
                                toast.error('Failed to cancel');
                              }
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/admissions/${admission.id}/retry-payment`);
                                // Open Razorpay
                                const options = {
                                  key: res.data.key_id,
                                  amount: res.data.amount,
                                  currency: res.data.currency,
                                  name: 'Iqra Skills',
                                  description: res.data.course_name,
                                  order_id: res.data.order_id,
                                  handler: async (response) => {
                                    try {
                                      await axios.post(`${API}/payment/verify-admission`, {
                                        razorpay_order_id: response.razorpay_order_id,
                                        razorpay_payment_id: response.razorpay_payment_id,
                                        razorpay_signature: response.razorpay_signature,
                                        admission_id: admission.id
                                      });
                                      toast.success('Payment successful!');
                                      fetchData();
                                    } catch (e) {
                                      toast.error('Payment verification failed');
                                    }
                                  },
                                  prefill: {
                                    name: res.data.user_name,
                                    email: res.data.user_email
                                  },
                                  theme: { color: '#3B82F6' }
                                };
                                const razorpay = new window.Razorpay(options);
                                razorpay.open();
                              } catch (e) {
                                toast.error('Failed to initiate payment');
                              }
                            }}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" /> Retry Payment
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {enrollments.length === 0 && admissions.filter(a => a.status === 'confirmed').length === 0 ? (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">You haven't enrolled in any courses yet</p>
                  <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* LMS Admissions - Confirmed courses */}
                  {admissions.filter(a => a.status === 'confirmed').map(admission => {
                    // Check if exam result exists for this admission
                    const examResult = results.find(r => 
                      r.course_name?.toLowerCase() === admission.course_name?.toLowerCase() ||
                      r.course_id === admission.course_id
                    );
                    
                    // Convert course name to slug for dynamic exam
                    const courseSlug = admission.course_name
                      ?.toLowerCase()
                      .replace(/\s+/g, '-')
                      .replace(/[^a-z0-9-]/g, '');
                    
                    return (
                      <div key={admission.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-4">
                          <h4 className="font-bold text-white">{admission.course_name}</h4>
                          <p className="text-purple-100 text-sm">
                            {admission.training_mode === 'RPL' ? 'RPL - Assessment Only' : 'STT - Full Training'}
                          </p>
                        </div>
                        <div className="p-4">
                          {/* Center Info for Training Center enrollments */}
                          {admission.enrollment_mode === 'center' && admission.center_id && (
                            <div className="bg-blue-50 rounded-lg p-3 mb-3 border border-blue-100">
                              <div className="flex items-center gap-2 text-sm">
                                <Building2 className="w-4 h-4 text-blue-600" />
                                <span className="font-medium text-blue-700">Training Center</span>
                              </div>
                              <p className="text-sm text-blue-800 mt-1">{admission.center_name || 'N/A'}</p>
                              {admission.center_code && (
                                <p className="text-xs text-blue-600 mt-0.5">Code: {admission.center_code}</p>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                            <Clock className="w-4 h-4" />
                            <span>Self-paced Learning</span>
                          </div>
                          
                          {examResult ? (
                            <div className={`p-3 rounded-lg mb-3 ${examResult.result === 'pass' ? 'bg-emerald-50' : 'bg-orange-50'}`}>
                              <div className="flex items-center gap-2">
                                {examResult.result === 'pass' ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-orange-600" />
                                )}
                                <span className={`font-semibold ${examResult.result === 'pass' ? 'text-emerald-700' : 'text-orange-700'}`}>
                                  {examResult.result === 'pass' ? 'PASSED' : 'FAILED'} - {examResult.percentage?.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 rounded-lg bg-emerald-50 mb-3">
                              <span className="text-emerald-700 font-medium flex items-center gap-2">
                                <CheckCircle className="w-4 h-4" /> Enrolled & Active
                              </span>
                            </div>
                          )}
                          
                          <div className="space-y-2">
                            {!examResult && (
                              <Button 
                                className="w-full bg-blue-600 hover:bg-blue-700"
                                onClick={() => navigate(`/student/dynamic-exam?course=${courseSlug}&mode=${admission.training_mode || 'STT'}`)}
                              >
                                <ClipboardCheck className="w-4 h-4 mr-2" /> Take Exam
                              </Button>
                            )}
                            {examResult?.result === 'pass' && (
                              <Button 
                                className="w-full bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => navigate(`/student/certificates`)}
                              >
                                <Award className="w-4 h-4 mr-2" /> View Certificate
                              </Button>
                            )}
                            <Button 
                              variant="outline"
                              className="w-full"
                              onClick={() => navigate('/student/my-courses')}
                            >
                              <PlayCircle className="w-4 h-4 mr-2" /> Continue Learning
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Assessment Enrollments */}
                  {enrollments.map(enrollment => {
                    const data = getEnrollmentWithCourse(enrollment);
                    const result = results.find(r => r.course_id === data.course?.id);
                    
                    return (
                      <div key={enrollment.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-4">
                          <h4 className="font-bold text-white">{data.course?.name || 'Course'}</h4>
                          <p className="text-blue-100 text-sm">{enrollment.enrollment_type} Program</p>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                            <Clock className="w-4 h-4" />
                            <span>{data.course?.duration || 'N/A'}</span>
                          </div>
                          
                          {result ? (
                            <div className={`p-3 rounded-lg mb-3 ${result.result === 'pass' ? 'bg-emerald-50' : 'bg-orange-50'}`}>
                              <div className="flex items-center gap-2">
                                {result.result === 'pass' ? (
                                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-orange-600" />
                                )}
                                <span className={`font-semibold ${result.result === 'pass' ? 'text-emerald-700' : 'text-orange-700'}`}>
                                  {result.result === 'pass' ? 'PASSED' : 'FAILED'} - {result.percentage?.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 rounded-lg bg-blue-50 mb-3">
                              <span className="text-blue-700 font-medium flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" /> Exam Pending
                              </span>
                            </div>
                          )}
                          
                          {!result ? (
                            <Button 
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={() => navigate(`/student/exams`)}
                            >
                              <PlayCircle className="w-4 h-4 mr-2" /> Start Exam
                            </Button>
                          ) : result.result === 'pass' ? (
                            <Button 
                              className="w-full bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => navigate(`/student/certificates`)}
                            >
                              <Award className="w-4 h-4 mr-2" /> View Certificate
                            </Button>
                          ) : (
                            <Button className="w-full bg-orange-600 hover:bg-orange-700">
                              <RefreshCw className="w-4 h-4 mr-2" /> Reassessment
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* LMS Video Courses Tab */}
          <TabsContent value="lms-courses">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <PlayCircle className="w-5 h-5 text-purple-500" />
                Video Learning (LMS)
              </h3>
              
              {lmsEnrollments.length > 0 && (
                <div className="mb-8">
                  <h4 className="text-sm font-medium text-slate-600 mb-4">Continue Learning</h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {lmsEnrollments.map(enrollment => (
                      <div key={enrollment.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                        <div className="aspect-video bg-slate-100 relative">
                          {enrollment.course?.thumbnail_url ? (
                            <img src={enrollment.course.thumbnail_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <PlayCircle className="w-12 h-12 text-slate-300" />
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-200">
                            <div className="h-full bg-purple-600" style={{ width: `${enrollment.progress_percent || 0}%` }}></div>
                          </div>
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-slate-800 line-clamp-1">{enrollment.course_title}</h4>
                          <p className="text-sm text-slate-500 mt-1">{enrollment.progress_percent?.toFixed(0) || 0}% Complete</p>
                          <Button onClick={() => navigate(`/student/learn/${enrollment.id}`)} className="w-full mt-3 bg-purple-600 hover:bg-purple-700">
                            Continue Learning
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {lmsCourses.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-600 mb-4">Browse Courses</h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {lmsCourses.filter(c => !lmsEnrollments.find(e => e.course_id === c.id)).slice(0, 6).map(course => (
                      <div key={course.id} className="border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                        <div className="aspect-video bg-slate-100 relative">
                          <PlayCircle className="w-12 h-12 text-slate-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                          <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-medium ${course.is_free ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                            {course.is_free ? 'FREE' : `₹${course.discount_price || course.price}`}
                          </span>
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-slate-800 line-clamp-1">{course.title}</h4>
                          <p className="text-sm text-slate-500">{course.instructor_name}</p>
                          <Button className="w-full mt-3 bg-purple-600 hover:bg-purple-700">
                            {course.is_free ? 'Enroll Free' : `Enroll - ₹${course.discount_price || course.price}`}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Fees & Payment Tab */}
          <TabsContent value="fees">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-6 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-amber-500" />
                Fees & Payment
              </h3>
              
              {/* Fee Summary */}
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-sm text-slate-500">Total Fees</p>
                  <p className="text-2xl font-bold text-slate-800">₹{(totalFeesPaid + totalFeesPending).toLocaleString()}</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl text-center">
                  <p className="text-sm text-emerald-600">Paid Amount</p>
                  <p className="text-2xl font-bold text-emerald-700">₹{totalFeesPaid.toLocaleString()}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-xl text-center">
                  <p className="text-sm text-amber-600">Pending Amount</p>
                  <p className="text-2xl font-bold text-amber-700">₹{totalFeesPending.toLocaleString()}</p>
                </div>
              </div>
              
              {studentFees.length === 0 ? (
                <div className="text-center py-12">
                  <Wallet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No fee records found</p>
                  <Button onClick={() => navigate('/student/admission')}>Take Admission</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {studentFees.map(fee => (
                    <div key={fee.id} className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-4 text-white">
                        <div className="flex justify-between items-center">
                          <div>
                            <h4 className="font-bold">{fee.course_name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-amber-100 text-sm">{fee.enrollment_mode === 'online' ? 'Online' : fee.center_name}</p>
                              {fee.installment_plan === 'two' && (
                                <span className="px-2 py-0.5 bg-purple-500 rounded text-xs font-medium">
                                  2 Installments (60:40)
                                </span>
                              )}
                              {fee.training_mode && (
                                <span className="px-2 py-0.5 bg-blue-500 rounded text-xs font-medium">
                                  {fee.training_mode}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            fee.payment_status === 'paid' ? 'bg-emerald-500' :
                            fee.payment_status === 'partial' ? 'bg-blue-500' : 'bg-white/20'
                          }`}>
                            {fee.payment_status?.toUpperCase() || 'PENDING'}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          {/* Installment 1 */}
                          <div className={`p-4 rounded-xl border-2 ${fee.installment_1_status === 'paid' ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium">1st Installment (60%)</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${fee.installment_1_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                {fee.installment_1_status}
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-slate-800">₹{fee.installment_1_amount?.toLocaleString()}</p>
                            {fee.installment_1_status !== 'paid' && (
                              <Button 
                                onClick={() => handleFeeInstallmentPayment(fee, 1)}
                                disabled={payingInstallment === `${fee.id}-1`}
                                className="w-full mt-3 bg-amber-600 hover:bg-amber-700"
                                size="sm"
                              >
                                {payingInstallment === `${fee.id}-1` ? 'Processing...' : 'Pay Now'}
                              </Button>
                            )}
                          </div>
                          
                          {/* Installment 2 */}
                          <div className={`p-4 rounded-xl border-2 ${fee.installment_2_status === 'paid' ? 'border-emerald-200 bg-emerald-50' : fee.installment_2_status === 'not_applicable' ? 'border-slate-100 bg-slate-50 opacity-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium">2nd Installment (40%)</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                fee.installment_2_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 
                                fee.installment_2_status === 'not_applicable' ? 'bg-slate-100 text-slate-500' :
                                'bg-amber-100 text-amber-700'
                              }`}>
                                {fee.installment_2_status === 'not_applicable' ? 'N/A' : fee.installment_2_status}
                              </span>
                            </div>
                            <p className="text-2xl font-bold text-slate-800">₹{fee.installment_2_amount?.toLocaleString() || '0'}</p>
                            {fee.installment_2_status === 'pending' && (
                              <Button 
                                onClick={() => handleFeeInstallmentPayment(fee, 2)}
                                disabled={payingInstallment === `${fee.id}-2` || fee.installment_1_status !== 'paid'}
                                className={`w-full mt-3 ${fee.installment_1_status !== 'paid' ? 'bg-slate-400' : 'bg-amber-600 hover:bg-amber-700'}`}
                                size="sm"
                              >
                                {payingInstallment === `${fee.id}-2` ? 'Processing...' : 
                                 fee.installment_1_status !== 'paid' ? 'Pay 1st Installment First' : 'Pay Now'}
                              </Button>
                            )}
                            {fee.installment_2_status === 'not_applicable' && (
                              <p className="text-xs text-slate-500 mt-2">Full payment completed</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Yearly Fees Tab - Distance Education Only */}
          <TabsContent value="yearly-fees">
            <div className="space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-6 text-white">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <Wallet className="w-7 h-7" />
                  Yearly Fee Deposit / वार्षिक शुल्क जमा
                </h2>
                <p className="mt-2 text-purple-100">
                  Distance Education courses ke liye yearly installment payment
                </p>
              </div>

              {distanceEnrollments.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="w-10 h-10 text-purple-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    No Distance Education Courses
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Aapke paas abhi koi Distance Education (Degree/Diploma) course enrollment nahi hai.
                  </p>
                  <Link to="/courses">
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <BookOpen className="w-4 h-4 mr-2" />
                      Browse Degree Programs
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-6">
                  {distanceEnrollments.map((enrollment) => (
                    <div key={enrollment.enrollment_id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                      {/* Course Header */}
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-5 border-b border-purple-100">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                              <GraduationCap className="w-5 h-5 text-purple-600" />
                              {enrollment.course_name}
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {enrollment.duration} ({enrollment.duration_years} Years)
                              </span>
                              {enrollment.center_name && (
                                <span className="flex items-center gap-1">
                                  <Building2 className="w-4 h-4" />
                                  {enrollment.center_code}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Total Course Fee</p>
                            <p className="text-2xl font-bold text-purple-700">₹{enrollment.total_fee?.toLocaleString()}</p>
                          </div>
                        </div>
                      </div>

                      {/* Fee Summary Bar */}
                      <div className="px-5 py-3 bg-slate-50 border-b flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-6">
                          <div>
                            <span className="text-xs text-slate-500">Yearly Fee</span>
                            <p className="font-semibold text-slate-800">₹{enrollment.yearly_fee?.toLocaleString()}/Year</p>
                          </div>
                          <div>
                            <span className="text-xs text-emerald-600">Total Paid</span>
                            <p className="font-semibold text-emerald-700">₹{enrollment.total_paid?.toLocaleString()}</p>
                          </div>
                          <div>
                            <span className="text-xs text-amber-600">Balance</span>
                            <p className="font-semibold text-amber-700">₹{enrollment.balance?.toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                              style={{ width: `${(enrollment.total_paid / enrollment.total_fee) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-slate-600">
                            {Math.round((enrollment.total_paid / enrollment.total_fee) * 100)}%
                          </span>
                        </div>
                      </div>

                      {/* Year-wise Payment Status */}
                      <div className="p-5">
                        <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                          <Receipt className="w-5 h-5 text-purple-500" />
                          Year-wise Payment Status / वर्षवार भुगतान स्थिति
                        </h4>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {enrollment.year_status?.map((yearData) => (
                            <div 
                              key={yearData.year}
                              className={`p-4 rounded-xl border-2 transition-all ${
                                yearData.status === 'paid' 
                                  ? 'bg-emerald-50 border-emerald-200' 
                                  : 'bg-white border-slate-200 hover:border-purple-300'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-lg font-bold text-slate-800">Year {yearData.year}</span>
                                {yearData.status === 'paid' ? (
                                  <span className="flex items-center gap-1 text-emerald-600 text-sm font-medium">
                                    <CheckCircle className="w-4 h-4" />
                                    Paid
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-amber-600 text-sm font-medium">
                                    <AlertCircle className="w-4 h-4" />
                                    Pending
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-xl font-bold text-slate-900 mb-2">
                                ₹{yearData.amount?.toLocaleString()}
                              </p>
                              
                              {yearData.status === 'paid' ? (
                                <div className="space-y-1 text-sm text-slate-600">
                                  <p className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {yearData.paid_date ? new Date(yearData.paid_date).toLocaleDateString('en-IN') : 'N/A'}
                                  </p>
                                  {yearData.transaction_id && (
                                    <p className="text-xs text-slate-500 truncate">
                                      Txn: {yearData.transaction_id}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <Button
                                  onClick={() => handleYearlyFeePayment(enrollment, yearData.year, yearData.amount)}
                                  disabled={payingYear === `${enrollment.enrollment_id}-${yearData.year}`}
                                  className="w-full mt-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                                >
                                  {payingYear === `${enrollment.enrollment_id}-${yearData.year}` ? (
                                    <>
                                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <CreditCard className="w-4 h-4 mr-2" />
                                      Pay Now
                                    </>
                                  )}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Payment History Section */}
                  {distancePayments.length > 0 && (
                    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-purple-500" />
                        Payment History / भुगतान इतिहास
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Year</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Amount</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Method</th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Transaction ID</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {distancePayments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-slate-50">
                                <td className="px-4 py-3 text-sm">
                                  {new Date(payment.paid_date).toLocaleDateString('en-IN')}
                                </td>
                                <td className="px-4 py-3">
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                    Year {payment.year}
                                  </span>
                                </td>
                                <td className="px-4 py-3 font-semibold text-emerald-600">
                                  ₹{payment.amount?.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600 capitalize">
                                  {payment.payment_method}
                                </td>
                                <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                                  {payment.transaction_id || payment.receipt_number || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Exams & Certificates Tab */}
          <TabsContent value="exams">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Exam Status */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-blue-500" />
                  Exam Status
                </h3>
                
                {results.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No exams taken yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {results.map(result => {
                      const data = getResultWithCourse(result);
                      return (
                        <div key={result.id} className={`p-4 rounded-xl border-2 ${result.result === 'pass' ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-slate-800">{data.course?.name || result.course_name}</h4>
                              <p className="text-sm text-slate-500">Score: {result.percentage?.toFixed(0)}%</p>
                            </div>
                            <div className="text-right">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${result.result === 'pass' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                                {result.result === 'pass' ? 'PASSED' : 'FAILED'}
                              </span>
                              {result.result === 'pass' && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="mt-2"
                                  onClick={() => navigate(`/student/certificates`)}
                                >
                                  <Download className="w-3 h-3 mr-1" /> Certificate
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Certificates */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-500" />
                  My Certificates
                </h3>
                
                {passedCourses.length === 0 ? (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No certificates earned yet</p>
                    <p className="text-sm text-slate-400 mt-1">Pass an exam to get certified!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {passedCourses.map(result => {
                      const data = getResultWithCourse(result);
                      return (
                        <div key={result.id} className="p-4 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl border border-emerald-200">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-xl flex items-center justify-center">
                              <Award className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-800">{data.course?.name || result.course_name}</h4>
                              <p className="text-sm text-emerald-600">Score: {result.percentage?.toFixed(0)}%</p>
                            </div>
                            <Button 
                              size="sm"
                              onClick={() => navigate(`/student/certificates`)}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Download className="w-4 h-4 mr-1" /> Download
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Education Tab */}
          <TabsContent value="education">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    My Education Details
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Your education details are used for course eligibility verification
                  </p>
                </div>
                <Button 
                  onClick={() => setShowEducationForm(!showEducationForm)}
                  variant={showEducationForm ? "outline" : "default"}
                  className={showEducationForm ? "" : "bg-blue-600 hover:bg-blue-700"}
                >
                  <PenLine className="w-4 h-4 mr-2" />
                  {showEducationForm ? 'Cancel' : (educationData?.highest_qualification ? 'Edit Education' : 'Add Education')}
                </Button>
              </div>

              {showEducationForm ? (
                <EducationForm 
                  initialData={educationData}
                  onUpdate={() => {
                    fetchEducation();
                    setShowEducationForm(false);
                  }}
                />
              ) : educationData?.highest_qualification ? (
                <div className="space-y-4">
                  {/* Qualification Summary */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <p className="text-sm text-slate-600">Highest Qualification</p>
                    <p className="text-xl font-bold text-blue-700 capitalize">
                      {educationData.highest_qualification === '10th' ? '10th (High School)' :
                       educationData.highest_qualification === '12th' ? '12th (Intermediate)' :
                       educationData.highest_qualification === 'graduation' ? 'Graduation' :
                       educationData.highest_qualification === 'post_graduation' ? 'Post Graduation' :
                       educationData.highest_qualification}
                    </p>
                  </div>

                  {/* Education Details Cards */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {educationData.education_details?.map((edu, index) => (
                      <div 
                        key={index}
                        className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 capitalize">
                              {edu.level === '10th' ? '10th (High School)' :
                               edu.level === '12th' ? '12th (Intermediate)' :
                               edu.level === 'graduation' ? 'Graduation' :
                               edu.level === 'post_graduation' ? 'Post Graduation' : edu.level}
                            </p>
                            <p className="text-xs text-slate-500">{edu.board_or_university}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-slate-500 text-xs">Passing Year</p>
                            <p className="font-medium text-slate-800">{edu.passing_year}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2">
                            <p className="text-slate-500 text-xs">Percentage</p>
                            <p className="font-medium text-slate-800">{edu.percentage}%</p>
                          </div>
                          {edu.total_marks && (
                            <div className="bg-slate-50 rounded-lg p-2 col-span-2">
                              <p className="text-slate-500 text-xs">Total Marks</p>
                              <p className="font-medium text-slate-800">{edu.total_marks}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-lg font-semibold text-slate-700 mb-2">No Education Details Added</h4>
                  <p className="text-slate-500 mb-4">
                    Add your education details to check course eligibility and complete your profile
                  </p>
                  <Button onClick={() => setShowEducationForm(true)} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Education Details
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Jobs & Placement Tab */}
          <TabsContent value="jobs">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-500" />
                  Recommended Jobs
                </h3>
                <Button onClick={() => navigate('/jobs')} variant="outline">View All Jobs</Button>
              </div>
              
              {recommendedJobs.length === 0 ? (
                <div className="text-center py-12">
                  <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No jobs available right now</p>
                  <Button onClick={() => navigate('/jobs')}>Browse Job Portal</Button>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendedJobs.slice(0, 6).map(job => (
                    <div key={job.id} className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-slate-800">{job.position_name || job.title}</h4>
                          <p className="text-sm text-slate-500">{job.company_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <IndianRupee className="w-3 h-3" /> {job.salary_range || (job.min_ctc ? `${job.min_ctc} - ${job.max_ctc} LPA` : 'Not disclosed')}
                        </span>
                      </div>
                      <Button 
                        onClick={() => {
                          if (!profileData?.is_complete) {
                            toast.error('Pehle apni Profile complete karein, fir job apply kar sakte hain', {
                              description: 'Profile update page par redirect kiya jaa raha hai...',
                              duration: 4000
                            });
                            setTimeout(() => navigate('/student/profile'), 800);
                            return;
                          }
                          navigate('/jobs');
                        }}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        size="sm"
                        data-testid={`apply-now-${job.id}`}
                      >
                        Apply Now
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Interview Calls Section */}
              {myInterviews.length > 0 && (
                <div className="mb-8 pb-6 border-b border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    Interview Calls ({myInterviews.length})
                  </h4>
                  <div className="space-y-3">
                    {myInterviews.map(interview => (
                      <div key={interview.id} className="border border-purple-200 bg-purple-50/50 rounded-xl p-4" data-testid={`student-interview-${interview.id}`}>
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center shrink-0">
                            {interview.interview_type === 'online' || interview.interview_type === 'Online' ? 
                              <Video className="w-5 h-5 text-purple-600" /> : 
                              <MapPin className="w-5 h-5 text-purple-600" />
                            }
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-slate-800">
                              {interview.job_title || 'Interview'} 
                              {interview.company_name && <span className="text-slate-500 font-normal"> at {interview.company_name}</span>}
                            </h5>
                            <div className="flex flex-wrap gap-3 mt-2 text-sm">
                              <span className="flex items-center gap-1 text-slate-600">
                                <CalendarDays className="w-4 h-4 text-purple-500" />
                                <strong>{interview.interview_date}</strong>
                              </span>
                              <span className="flex items-center gap-1 text-slate-600">
                                <Clock className="w-4 h-4 text-purple-500" />
                                <strong>{interview.interview_time}</strong>
                              </span>
                              <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                interview.interview_type === 'online' || interview.interview_type === 'Online'
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-orange-100 text-orange-700'
                              }`}>
                                {interview.interview_type === 'online' || interview.interview_type === 'Online' ? 'Online' : 'In-Person'}
                              </span>
                            </div>
                            {/* In-Person Location */}
                            {interview.interview_type !== 'online' && interview.interview_type !== 'Online' && (interview.interview_location || interview.job_location) && (
                              <div className="mt-2 flex items-center gap-1 text-sm text-orange-600 bg-orange-50 px-3 py-1.5 rounded-lg">
                                <MapPin className="w-4 h-4" />
                                <span><strong>Location:</strong> {interview.interview_location || interview.job_location}</span>
                              </div>
                            )}
                            {/* Online Meeting Link */}
                            {(interview.interview_type === 'online' || interview.interview_type === 'Online') && interview.meeting_link && (
                              <a href={interview.meeting_link} target="_blank" rel="noreferrer" 
                                className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors">
                                <ExternalLink className="w-4 h-4" />
                                Join Meeting
                              </a>
                            )}
                            {interview.notes && (
                              <p className="text-xs text-slate-500 mt-2 italic">Note: {interview.notes}</p>
                            )}
                          </div>
                          <span className={`shrink-0 px-2 py-1 rounded-full text-xs font-medium ${
                            interview.status === 'scheduled' ? 'bg-amber-100 text-amber-700' :
                            interview.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {interview.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Placement Section */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-4">Track Your Placements</h4>
                <Button onClick={() => navigate('/student/placements')} variant="outline" className="w-full">
                  <Target className="w-4 h-4 mr-2" /> View Placement Dashboard
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Profile Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="text-center mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-4xl text-white font-bold">{user?.name?.charAt(0) || 'S'}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">{user?.name || 'Student'}</h3>
                  <p className="text-slate-500">{user?.email}</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <Mail className="w-5 h-5 text-slate-400" />
                    <span className="text-sm text-slate-600">{user?.email}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <Phone className="w-5 h-5 text-slate-400" />
                    <span className="text-sm text-slate-600">{user?.phone || 'Not added'}</span>
                  </div>
                </div>
                
                <Button onClick={() => navigate('/student/profile')} className="w-full mt-4" variant="outline">
                  Edit Profile
                </Button>
              </div>

              {/* Skills & Education */}
              <div className="lg:col-span-2 space-y-6">
                {/* Skills */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {passedCourses.length > 0 ? (
                      passedCourses.map(result => {
                        const data = getResultWithCourse(result);
                        return (
                          <span key={result.id} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                            {data.course?.name || result.course_name}
                          </span>
                        );
                      })
                    ) : (
                      <p className="text-slate-500">Complete courses to add skills</p>
                    )}
                  </div>
                </div>

                {/* Education / Courses Completed */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-purple-500" />
                    Courses Completed
                  </h3>
                  {passedCourses.length === 0 ? (
                    <p className="text-slate-500">No courses completed yet</p>
                  ) : (
                    <div className="space-y-3">
                      {passedCourses.map(result => {
                        const data = getResultWithCourse(result);
                        return (
                          <div key={result.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                            <div className="flex items-center gap-3">
                              <BadgeCheck className="w-5 h-5 text-emerald-500" />
                              <span className="font-medium text-slate-800">{data.course?.name || result.course_name}</span>
                            </div>
                            <span className="text-emerald-600 font-medium">{result.percentage?.toFixed(0)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Auto Resume Generate */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold mb-2">Auto Generate Resume</h3>
                      <p className="text-white/80">Create a professional resume with your skills and certificates</p>
                    </div>
                    <Button 
                      onClick={() => navigate('/student/cv-builder')}
                      className="bg-white text-purple-600 hover:bg-white/90"
                    >
                      <FileText className="w-4 h-4 mr-2" /> Generate CV
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        </div>
      </main>
    </div>
  );
};

export default StudentDashboard;
